/**
 * Maps US state abbreviations to IANA timezone identifiers.
 * States that span multiple timezones use the most populated timezone.
 */
const STATE_TO_TIMEZONE: Record<string, string> = {
  // Eastern
  CT: 'America/New_York',
  DE: 'America/New_York',
  DC: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/New_York',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NY: 'America/New_York',
  NC: 'America/New_York',
  OH: 'America/New_York',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WV: 'America/New_York',
  // Central
  AL: 'America/Chicago',
  AR: 'America/Chicago',
  IL: 'America/Chicago',
  IA: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/Chicago',
  LA: 'America/Chicago',
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  NE: 'America/Chicago',
  OK: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  WI: 'America/Chicago',
  // Mountain
  AZ: 'America/Phoenix',
  CO: 'America/Denver',
  ID: 'America/Boise',
  MT: 'America/Denver',
  NM: 'America/Denver',
  UT: 'America/Denver',
  WY: 'America/Denver',
  // Pacific
  CA: 'America/Los_Angeles',
  NV: 'America/Los_Angeles',
  OR: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
  // Alaska & Hawaii
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
  // Territories
  PR: 'America/Puerto_Rico',
  GU: 'Pacific/Guam',
  VI: 'America/Virgin',
  AS: 'Pacific/Pago_Pago',
  // Multi-timezone states: use most populated zone
  IN: 'America/Indiana/Indianapolis',
  ND: 'America/Chicago',
  SD: 'America/Chicago'
}

/**
 * Get the IANA timezone for a US state abbreviation.
 * Returns null for unknown states or international addresses.
 */
export function timezoneForState(state: string | undefined): string | null {
  if (!state) return null
  return STATE_TO_TIMEZONE[state.toUpperCase()] || null
}

/**
 * Get the friendly short timezone name for a given IANA timezone (e.g. "PST", "EDT").
 */
function friendlyTzName(date: Date, timezone?: string): string {
  const opts: Intl.DateTimeFormatOptions = { timeZoneName: 'short' }
  if (timezone) opts.timeZone = timezone
  return (
    new Intl.DateTimeFormat('en-US', opts)
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value || ''
  )
}

/**
 * Format a date in a specific timezone with a friendly zone label (e.g. "PST", "EDT").
 */
function formatInTimezone(date: Date, timezone: string): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
  return `${formatted} ${friendlyTzName(date, timezone)}`
}

/**
 * Format a verbose date string for hover tooltips.
 *
 * If a destination timezone is provided and differs from the user's local timezone,
 * shows dual time: local first, then destination.
 *
 * Examples:
 *   "Mon, Mar 30, 2026, 5:32 AM PDT"
 *   "Mon, Mar 30, 2026, 5:32 AM PDT (8:32 AM EDT at destination)"
 */
export function formatDateVerboseWithDestination(
  date: string | Date,
  destinationTimezone: string | null
): string {
  const d = date instanceof Date ? date : new Date(date)
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const localLine = formatInTimezone(d, localTz)

  if (!destinationTimezone) return localLine

  const localTzName = friendlyTzName(d)
  const destTzName = friendlyTzName(d, destinationTimezone)

  // Same timezone — no need for dual display
  if (localTzName === destTzName) return localLine

  const destTime = new Intl.DateTimeFormat('en-US', {
    timeZone: destinationTimezone,
    hour: 'numeric',
    minute: '2-digit'
  }).format(d)

  return `${localLine} (${destTime} ${destTzName} at destination)`
}
